import { useEffect, useId, useRef, useState } from "preact/hooks";
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { api, ApiError } from "../lib/api-client";
import { formatTimestamp } from "../lib/format";
import { useConfirm } from "../lib/use-confirm";
import { useModalDialog } from "../lib/use-modal-dialog";
import { Alert, Button, Card, CardBody, CardFooter, CardHeader, SegmentedControl } from "./ui";
import { MarkdownPreview } from "./markdown-preview";
import type {
  ContentDetailDto,
  ContentRevisionDetailDto,
  ContentRevisionSummaryDto,
} from "@shared/types";

type Props = {
  projectId: string;
  slug: string;
  // Notify the parent that the content body was overwritten by a restore.
  // Parent is responsible for re-syncing its local draft / preview state.
  onRestored: (detail: ContentDetailDto) => void;
  // Render the "プレビュー" button + preview modal. Advanced editor uses it,
  // the simple chat view doesn't.
  showPreview?: boolean;
  // Bump to force a refetch (e.g. after a save).
  refreshKey?: unknown;
  // A restore snapshots the saved DB row, not the browser draft. Block it while
  // unsaved edits exist so those edits cannot be silently discarded.
  dirty?: boolean;
};



export function RevisionHistory({
  projectId,
  slug,
  onRestored,
  showPreview = false,
  refreshKey,
  dirty = false,
}: Props) {
  const [revisions, setRevisions] = useState<
    ContentRevisionSummaryDto[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] =
    useState<ContentRevisionDetailDto | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  // Mutation responses need the same stale guard as the list fetch. Updating
  // these refs during render makes a route-param change visible immediately,
  // before the next effect/cleanup runs.
  const identityRef = useRef("");
  const identity = `${projectId}\u0000${slug}`;
  identityRef.current = identity;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Drop the previous document's revisions the moment the target changes so a
  // stale cross-document list can't show (and be restored from) under the new
  // slug while the refetch is in flight. Keyed on the document identity only —
  // not refreshKey — so a post-save re-fetch of the same document doesn't flash
  // the list back to a skeleton.
  useEffect(() => {
    setRevisions(null);
    setError(null);
    setPreviewing(null);
    setRestoring(null);
  }, [projectId, slug]);

  useEffect(() => {
    let alive = true;
    setError(null);
    void (async () => {
      try {
        const res = await api.listRevisions(projectId, slug);
        if (!alive) return;
        setRevisions(res.items);
      } catch (e) {
        if (!alive) return;
        // A brand-new draft that hasn't been saved yet has no row in `contents`
        // and therefore no revisions. Treat 404 as "empty list", not an error.
        if (e instanceof ApiError && e.status === 404) {
          setRevisions([]);
        } else {
          setError((e as Error).message);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId, slug, refreshKey]);

  async function onPreview(rev: ContentRevisionSummaryDto) {
    const startedFor = identity;
    try {
      const detail = await api.getRevision(projectId, slug, rev.id);
      if (!mountedRef.current || identityRef.current !== startedFor) return;
      setPreviewing(detail);
    } catch (e) {
      if (!mountedRef.current || identityRef.current !== startedFor) return;
      setError((e as Error).message);
    }
  }

  async function onRestore(rev: ContentRevisionSummaryDto) {
    if (dirtyRef.current) {
      setError("未保存の変更を保存または破棄してから、リビジョンを復元してください。");
      return;
    }
    const ok = await confirm({
      title: `このリビジョンに戻しますか？`,
      description: (
        <>
          <span class="font-mono text-xs">{formatTimestamp(rev.createdAt)}</span>{" "}
          の内容で上書きします。現在の保存済み内容は新しいリビジョンとして記録されるので、ロールバック自体は取り消し可能です。
        </>
      ),
      confirmLabel: "この状態に戻す",
    });
    if (!ok || !mountedRef.current || dirtyRef.current) return;
    const startedFor = identity;
    setRestoring(rev.id);
    try {
      const res = await api.restoreRevision(projectId, slug, rev.id);
      if (!mountedRef.current || identityRef.current !== startedFor) return;
      // The editor remains interactive while the request is in flight. If the
      // user typed meanwhile, preserve that new draft instead of replacing it
      // with the restore response. The server-side restore is still reflected
      // in the refreshed revision list and the next save remains undoable.
      if (dirtyRef.current) {
        setRevisions(res.revisions);
        setError(
          "復元中に編集が行われたため、未保存の編集内容を画面に残しました。保存または破棄してから履歴を確認してください。",
        );
        return;
      }
      onRestored(res.content);
      setPreviewing(null);
      // Server returns the refreshed revisions list in the same response, so
      // no second fetch is needed (and no race with another concurrent edit).
      setRevisions(res.revisions);
    } catch (e) {
      if (!mountedRef.current || identityRef.current !== startedFor) return;
      setError((e as Error).message);
    } finally {
      if (mountedRef.current && identityRef.current === startedFor) setRestoring(null);
    }
  }

  return (
    <div class="space-y-3">
      {confirmDialog}
      {error && <Alert tone="danger">{error}</Alert>}
      {dirty && (
        <Alert tone="warning">
          リビジョンを復元する前に、未保存の変更を保存または破棄してください。
        </Alert>
      )}
      {revisions === null ? (
        <p class="text-xs text-fg-subtle">読み込み中…</p>
      ) : revisions.length === 0 ? (
        <p class="text-xs text-fg-subtle">
          まだリビジョンがありません。保存するたびに直前の状態が記録されます (最大
          10 件)。
        </p>
      ) : (
        <ul
          class={
            showPreview ? "divide-y divide-border-subtle" : "grid gap-2"
          }
        >
          {revisions.map((rev) => (
            <li
              key={rev.id}
              class={
                showPreview
                  ? "flex flex-wrap items-center justify-between gap-3 py-3"
                  : "flex flex-wrap items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
              }
            >
              <div class="min-w-0">
                <div class="font-mono text-xs text-fg-subtle">
                  {formatTimestamp(rev.createdAt)}
                  {rev.createdBy && (
                    <span class="ml-2 text-fg-faint">
                      by {rev.createdBy.email}
                    </span>
                  )}
                </div>
                <div class="mt-0.5 truncate text-sm text-fg">{rev.title}</div>
              </div>
              <div class="flex items-center gap-2">
                {showPreview && (
                  <Button
                    size="sm"
                    variant="secondary"
                    iconLeft={<EyeIcon class="h-3.5 w-3.5" />}
                    onClick={() => void onPreview(rev)}
                  >
                    プレビュー
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={showPreview ? "primary" : "secondary"}
                  iconLeft={
                    showPreview ? (
                      <ArrowPathIcon class="h-3.5 w-3.5" />
                    ) : (
                      <ArrowUturnLeftIcon class="h-3.5 w-3.5" />
                    )
                  }
                  disabled={restoring !== null || dirty}
                  loading={restoring === rev.id}
                  onClick={() => void onRestore(rev)}
                >
                  {restoring === rev.id ? "復元中…" : "この状態に戻す"}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showPreview && previewing && (
        <RevisionPreviewDialog
          revision={previewing}
          restoring={restoring === previewing.id}
          // Mirror the list-row gating: the list's warning Alert sits behind
          // the modal, so the dialog must disable restore on its own.
          restoreDisabled={
            dirty || (restoring !== null && restoring !== previewing.id)
          }
          dirty={dirty}
          onClose={() => setPreviewing(null)}
          // Detail extends Summary, so the existing list-row restore handler
          // works as-is without rebuilding a partial summary shape.
          onRestore={() => void onRestore(previewing)}
        />
      )}
    </div>
  );
}

type ViewMode = "rendered" | "source";

function RevisionPreviewDialog({
  revision,
  restoring,
  restoreDisabled,
  dirty,
  onClose,
  onRestore,
}: {
  revision: ContentRevisionDetailDto;
  restoring: boolean;
  restoreDisabled: boolean;
  dirty: boolean;
  onClose: () => void;
  onRestore: () => void;
}) {
  // showModal() handles Esc + focus trap natively; the hook mirrors its close
  // event into parent state so re-opening with a different revision tears down
  // and re-mounts cleanly.
  const ref = useModalDialog({ onClose });
  const [view, setView] = useState<ViewMode>("rendered");
  const titleId = useId();

  function onBackdropClick(e: MouseEvent) {
    // In modal mode the <dialog> element itself fills the viewport; clicks
    // inside the Card bubble with a descendant target. Target === dialog
    // means the click landed on the backdrop region.
    if (e.target === ref.current && !restoring) ref.current?.close();
  }

  return (
    <dialog
      ref={ref}
      onClick={onBackdropClick}
      aria-labelledby={titleId}
      // Tailwind preflight zeroes the UA `margin: auto` that `showModal()`
      // relies on for centering, so restore it explicitly with `m-auto`.
      class="m-auto w-[min(95vw,56rem)] max-h-[90vh] rounded-lg border-0 bg-transparent p-0 backdrop:bg-fg/40"
    >
      <Card elevated class="flex max-h-[90vh] flex-col">
        <CardHeader class="flex flex-wrap items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="font-mono text-xs text-fg-subtle">
              {formatTimestamp(revision.createdAt)}
              {revision.createdBy && (
                <span class="ml-2 text-fg-faint">
                  by {revision.createdBy.email}
                </span>
              )}
            </div>
            <h2 id={titleId} class="mt-0.5 truncate text-base font-semibold text-fg">
              {revision.title}
            </h2>
          </div>
          <SegmentedControl<ViewMode>
            ariaLabel="表示モード"
            size="sm"
            value={view}
            onChange={setView}
            options={[
              { value: "rendered", label: "プレビュー" },
              { value: "source", label: "ソース" },
            ]}
          />
        </CardHeader>
        <CardBody class="min-h-0 flex-1 overflow-auto bg-bg-subtle/40">
          {view === "rendered" ? (
            <MarkdownPreview source={revision.body} />
          ) : (
            <pre class="whitespace-pre-wrap break-words rounded-md border border-border bg-bg p-4 font-mono text-xs text-fg-muted">
              {revision.body}
            </pre>
          )}
        </CardBody>
        <CardFooter class="flex flex-wrap items-center justify-end gap-2">
          {dirty && (
            <p class="mr-auto text-xs text-warning-fg">
              未保存の変更を保存または破棄すると復元できます
            </p>
          )}
          <Button variant="secondary" size="sm" onClick={onClose} disabled={restoring}>
            閉じる
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconLeft={<ArrowPathIcon class="h-3.5 w-3.5" />}
            loading={restoring}
            disabled={restoreDisabled}
            onClick={onRestore}
          >
            {restoring ? "復元中…" : "この状態に戻す"}
          </Button>
        </CardFooter>
      </Card>
    </dialog>
  );
}
